import * as React from 'react';
import { List, Card, Divider } from '@mui/material';
import { HeroBanner } from '@brightlayer-ui/react-components/core';
import { MetricHero } from '../MetricHero';
import { ChannelDatum } from '../../types';
import { ActionRow } from '../OverviewCard/ActionRow';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { AppRoute } from '../../routes';
import { SiteReadingCardMetricListItem } from '../SiteReadingCardMetricListItem';
import { Data, isDataLoaded } from '../../types/data-providers';
import { DataIndicators } from '../DataIndicators';
import { ConnectionHero } from '../ConnectionHero';
import { BatteryHero } from '../BatteryHero/BatteryHero';

const HeroCardCount = 2;
//const TotalCount = 14;

export const SiteReadingCard: React.FC<{
    channelData: Data<ChannelDatum[]>;
    onlineStatusData: Data<object | undefined>;
    batteryStatusData: Data<boolean | null>;
    readingsAppRoute: AppRoute;
    testId?: string;
}> = (props) => {
    const { t } = useLanguageLocale();
    const priorityChannelData: ChannelDatum[] = [
        ...(isDataLoaded(props.channelData) ? props.channelData.value.slice(0, HeroCardCount) : []),
    ];

    const otherChannelData = isDataLoaded(props.channelData)
        ? props.channelData.value.slice(HeroCardCount, props.channelData.value.length)
        : [];

    const isEmpty = isDataLoaded(props.channelData) ? props.channelData.value.length === 0 : true;

    // Compute total tiles that will live in the hero banner so we can pick the
    // best layout: fill the row evenly when there are 1–4 tiles (so 2 tiles take
    // equal left/right halves, 4 tiles look balanced), and switch to fixed-width
    // tiles with horizontal scroll once there are 5 or more.
    const hasBatteryHero =
        isDataLoaded(props.batteryStatusData) && props.batteryStatusData.value !== null;
    const totalHeroCount = priorityChannelData.length + (hasBatteryHero ? 1 : 0) + 1; // +1 for ConnectionHero
    const fillEvenly = totalHeroCount <= 4;
    const fixedTileStyle: React.CSSProperties = fillEvenly
        ? { flex: '1 1 0', minWidth: 0, width: 'auto' }
        : { flex: '0 0 80px', width: 80 };

    return (
        <Card data-testid={props.testId} style={{ width: '360px' }}>
            <>
                <HeroBanner
                    limit={20}
                    sx={{
                        // 1–4 tiles → fill the row evenly (no scrollbar, balanced layout).
                        // 5+ tiles  → fixed-width tiles with horizontal scroll so long
                        //             labels stay fully readable.
                        flexWrap: 'nowrap',
                        overflowX: fillEvenly ? 'hidden' : 'auto',
                        overflowY: 'hidden',
                        justifyContent: fillEvenly ? 'space-between' : 'flex-start',
                        columnGap: 0,
                        py: 1,
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            borderRadius: 3,
                        },
                    }}
                >
                    {priorityChannelData.map((channelDatum, index) => (
                        <MetricHero
                            testId="channel-data-hero"
                            key={index}
                            channelDatum={channelDatum}
                            fillEvenly={fillEvenly}
                        />
                    ))}

                    {hasBatteryHero && (
                        <div style={fixedTileStyle}>
                            <BatteryHero siteBatteryStatus={props.batteryStatusData} testId="battery-data-hero" />
                        </div>
                    )}

                    <div style={fixedTileStyle}>
                        <ConnectionHero onlineStatusData={props.onlineStatusData} testId="connection-data-hero" />
                    </div>
                </HeroBanner>
                <Divider />
                <DataIndicators
                    emptyIcon="Maintenance"
                    emptyMessage={t('DASHBOARD.NO_DATA')}
                    errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                    data={props.channelData}
                    isEmpty={isEmpty}
                    inline
                />

                {!isEmpty && (
                    <>
                        <List disablePadding>
                            {otherChannelData.map((channelDatum, index) => (
                                <SiteReadingCardMetricListItem
                                    testId="channel-data-list-item"
                                    key={index}
                                    value={channelDatum}
                                    showIcon
                                    isLastItem={index + 1 === otherChannelData.length}
                                />
                            ))}
                        </List>
                        <Divider />
                        <ActionRow
                            label={t('DASHBOARD.SITE_DETAILS.READING_CARDS.VIEW_ALL')}
                            appRoute={props.readingsAppRoute}
                        />
                    </>
                )}
            </>
        </Card>
    );
};
